import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { MultiSelectModule } from 'primeng/multiselect';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { DialogModule } from 'primeng/dialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToolbarModule } from 'primeng/toolbar';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { RippleModule } from 'primeng/ripple';
import { CurrencyService } from '../../../core/services/currency.service';
import { InvoicingProjectAccountService } from '../../../core/services/invoicing-project-account.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { InvoicingProjectAccountStatus } from '../../../core/enums/invoicing-project-account.status';
import {
  InvoicingProjectAccount,
  InvoicingProjectAccountRequest,
  ClientMiniDto,
  ProjectMiniDto,
} from '../../../core/models/invoicing-project-account.model';
import { Subscription, catchError, concatMap, finalize, from, of, switchMap, timer, toArray } from 'rxjs';
import * as XLSX from 'xlsx';
import { RegexConstants } from '../../../core/constants/regex-constants';
interface AccountClientGroup {
  client: string;
  projects: string[];
}
interface AccountRow {
  rowId: string;
  account: InvoicingProjectAccount;
  groups: AccountClientGroup[];
}
@Component({
  selector: 'em-invoicing-project-account',
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    ButtonModule,
    ReactiveFormsModule,
    CalendarModule,
    DropdownModule,
    MultiSelectModule,
    InputTextModule,
    ToastModule,
    DialogModule,
    ConfirmDialogModule,
    ToolbarModule,
    TagModule,
    TooltipModule,
    RippleModule,
    TranslateModule,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './invoicing-project-account.component.html',
  styleUrl: './invoicing-project-account.component.scss',
})
export class InvoicingProjectAccountComponent implements OnInit, OnDestroy {
  invoicingProjectAccounts: InvoicingProjectAccount[] = [];
  filteredAccounts: InvoicingProjectAccount[] = [];
  displayRows: AccountRow[] = [];
  expandedRowIds = new Set<string>();
  accountsLoading = false;
  form: FormGroup;
  filtersForm: FormGroup;
  showFilterRow = false;
  private readonly maxImportFileSizeBytes = 150 * 1024 * 1024;
  private readonly importChunkSize = 5000;
  private readonly projectAccountPattern = RegexConstants.PROJECT_ACCOUNT_IBBIOL_PATTERN;
  @ViewChild('ibbiolFileInput') ibbiolFileInput?: ElementRef<HTMLInputElement>;
  importInProgress = false;
  private importPollingSub?: Subscription;
  importProgressText: string | null = null;
  private lastImportedSeen = 0;
  private lastLiveRefreshAt = 0;
  editDialogVisible: boolean = false;
  isEditMode: boolean = false;
  currentId: number | null = null;
  ibbiolDialogVisible: boolean = false;
  ibbiolForm: FormGroup;
  /** Set when opened from Finance → Group Add combination (return navigation after save). */
  private returnGroupId: number | null = null;
  clientOptions: ClientMiniDto[] = [];
  projectOptions: ProjectMiniDto[] = [];
  allProjects: ProjectMiniDto[] = [];
  currencyOptions: { label: string; value: string }[] = [];
  statusOptions = Object.keys(InvoicingProjectAccountStatus).map((key) => ({
    label: key.charAt(0) + key.slice(1).toLowerCase(),
    value: InvoicingProjectAccountStatus[key as keyof typeof InvoicingProjectAccountStatus],
  }));
  constructor(
    private fb: FormBuilder,
    private accountService: InvoicingProjectAccountService,
    private currencyService: CurrencyService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private translate: TranslateService,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.form = this.fb.group({
      clientIds: [[], Validators.required],
      projectIds: [[], Validators.required],
      code: [null, [Validators.required, Validators.pattern(this.projectAccountPattern)]],
      description: [null],
      currency: [null],
      startDate: [new Date(), Validators.required],
      endDate: [null],
      status: [InvoicingProjectAccountStatus.ACTIVE, Validators.required],
    });
    this.ibbiolForm = this.fb.group({
      preProjectCode: [null, [Validators.required, Validators.pattern(this.projectAccountPattern)]],
    });
    this.filtersForm = this.fb.group({
      code: [''],
      statuses: [[]],
    });
  }
  ngOnInit() {
    this.loadAccounts();
    this.loadDropdowns();
    // The End Date drives the status: an account is Inactive as soon as its end
    // date is reached or has passed. This previously ran in reverse and silently
    // overwrote (or cleared) the End Date the user had entered.
    this.form.get('endDate')?.valueChanges.subscribe(() => this.syncStatusWithEndDate());
    this.syncStatusWithEndDate();
    this.applyIbbiolEntryFromRoute();
  }
  ngOnDestroy() {
    this.importPollingSub?.unsubscribe();
  }
  // ---------------------------------------------------------------------------
  // Table filtering / expandable rows
  // ---------------------------------------------------------------------------
  toggleFilters() {
    this.showFilterRow = !this.showFilterRow;
  }
  clearAllFilters() {
    this.filtersForm.reset({ code: '', statuses: [] });
    this.applyFilters();
  }
  applyFilters() {
    const { code, statuses } = this.filtersForm.value as {
      code: string | null;
      statuses: string[] | null;
    };
    const codeTerm = (code ?? '').trim().toLowerCase();
    const selectedStatuses = new Set((statuses ?? []).map((s) => String(s)));
    this.filteredAccounts = (this.invoicingProjectAccounts ?? []).filter((account) => {
      const matchesCode = !codeTerm || (account.code ?? '').toLowerCase().includes(codeTerm);
      const matchesStatus = selectedStatuses.size === 0 || selectedStatuses.has(String(account.status));
      return matchesCode && matchesStatus;
    });
    this.buildDisplayRows();
  }
  private refreshFilteredAccounts() {
    this.applyFilters();
  }
  /** One row per account (code). Clients and their projects live inside the row. */
  private buildDisplayRows() {
    const rows: AccountRow[] = [];
    for (const account of this.filteredAccounts ?? []) {
      const groups: AccountClientGroup[] = [];
      const groupByClient = new Map<string, AccountClientGroup>();
      for (const pair of this.getAccountProjectPairs(account)) {
        const client = pair.client ?? '-';
        const project = pair.project ?? '-';
        let group = groupByClient.get(client);
        if (!group) {
          group = { client, projects: [] };
          groupByClient.set(client, group);
          groups.push(group);
        }
        if (project && project !== '-' && !group.projects.includes(project)) {
          group.projects.push(project);
        }
      }
      rows.push({ rowId: String(account.id), account, groups });
    }
    this.displayRows = rows;
  }
  toggleRow(rowId: string) {
    if (this.expandedRowIds.has(rowId)) {
      this.expandedRowIds.delete(rowId);
    } else {
      this.expandedRowIds.add(rowId);
    }
  }
  isRowExpanded(rowId: string): boolean {
    return this.expandedRowIds.has(rowId);
  }
  /** Chips hold the One ERP client names only; a client without one produces no chip. */
  getClientChips(account: InvoicingProjectAccount): string[] {
    return this.toChips(account.oneErpClientNames, account.oneErpClientName);
  }
  getProjectChips(account: InvoicingProjectAccount): string[] {
    return this.toChips(account.projectNames, account.projectName);
  }
  private toChips(names: Array<string | null> | null | undefined, joined?: string | null): string[] {
    const fromArray = (names ?? [])
      .filter((name): name is string => typeof name === 'string')
      .map((name) => name.trim())
      .filter(Boolean);
    if (fromArray.length) {
      return [...new Set(fromArray)];
    }
    const fromJoined = (joined ?? '')
      .split(',')
      .map((name) => name.trim())
      .filter(Boolean);
    return [...new Set(fromJoined)];
  }
  getAccountProjectPairs(account: InvoicingProjectAccount): Array<{ client: string; project: string }> {
    const projectIds = account.projectIds ?? [];
    if (projectIds.length && this.allProjects?.length) {
      const projectsById = new Map(this.allProjects.map((project) => [project.id, project]));
      const pairs = projectIds
        .map((id) => projectsById.get(id))
        .filter((project): project is ProjectMiniDto => !!project)
        .map((project) => ({
          client: this.resolveClientDisplayName(project.client),
          project: project.name,
        }));
      if (pairs.length) {
        return pairs;
      }
    }
    const projects = this.getProjectChips(account);
    const clients = this.getClientChips(account);
    return projects.map((project, index) => ({
      project,
      client: clients[index] ?? clients[0] ?? '-',
    }));
  }
  /**
   * The expanded project account table shows the One ERP client name.
   * The regular client name is never substituted for it: a client without an ERP
   * name renders as "-".
   */
  private resolveClientDisplayName(
    client: { oneErpClientName?: string | null } | null | undefined,
  ): string {
    return (client?.oneErpClientName ?? '').trim() || '-';
  }
  /** True when the end date is set and is today or in the past. */
  isEndDateReached(endDate: Date | string | null | undefined): boolean {
    if (!endDate) return false;
    const end = new Date(endDate);
    if (isNaN(end.getTime())) return false;
    end.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return end.getTime() <= today.getTime();
  }
​
  /** Keeps the Status field in sync with the End Date. ARCHIVED is left untouched. */
  private syncStatusWithEndDate(): void {
    const statusControl = this.form.get('status');
    if (!statusControl) return;
    if (String(statusControl.value) === InvoicingProjectAccountStatus.ARCHIVED) return;
    const derived = this.isEndDateReached(this.form.get('endDate')?.value)
      ? InvoicingProjectAccountStatus.INACTIVE
      : InvoicingProjectAccountStatus.ACTIVE;
    if (String(statusControl.value) !== derived) {
      statusControl.setValue(derived, { emitEvent: false });
    }
  }
​
  /** True when the End Date locks the status to Inactive. */
  get isStatusLockedByEndDate(): boolean {
    return this.isEndDateReached(this.form?.get('endDate')?.value);
  }
​
  /**
   * Status options for the create/edit dialog. Active is disabled once the End
   * Date is reached, so the derived Inactive status cannot be overridden.
   * The unfiltered `statusOptions` is still used by the table filter.
   */
  get formStatusOptions(): Array<{ label: string; value: string; disabled: boolean }> {
    const locked = this.isStatusLockedByEndDate;
    return this.statusOptions.map((option) => ({
      ...option,
      disabled: locked && option.value === InvoicingProjectAccountStatus.ACTIVE,
    }));
  }
​
  getStatusSeverity(status: InvoicingProjectAccountStatus | string): string {
    switch (String(status)) {
      case InvoicingProjectAccountStatus.ACTIVE:
        return 'success';
      case InvoicingProjectAccountStatus.INACTIVE:
        return 'warning';
      case InvoicingProjectAccountStatus.ARCHIVED:
        return 'secondary';
      default:
        return 'info';
    }
  }
  isAccountActive(account: InvoicingProjectAccount): boolean {
    return String(account.status) === InvoicingProjectAccountStatus.ACTIVE;
  }
  toggleAccountStatus(account: InvoicingProjectAccount) {
    if (this.isAccountActive(account)) {
      this.deactivateAccount(account);
    } else {
      this.activateAccount(account);
    }
  }
  loadAccounts() {
    this.accountsLoading = true;
    this.accountService
      .getAllAccounts()
      .pipe(
        finalize(() => {
          this.accountsLoading = false;
        }),
      )
      .subscribe({
        next: (data) => {
          this.invoicingProjectAccounts = this.enrichAccounts(data ?? []);
          this.refreshFilteredAccounts();
        },
        error: (err: any) => console.error('Failed to load accounts', err),
      });
  }
  loadDropdowns() {
    this.accountService.getClientsForDropdown().subscribe({
      next: (data) => {
        this.clientOptions = (data ?? []).map((client) => ({
          ...client,
          displayName: (client.oneErpClientName ?? '').trim() || client.name,
        }));
        this.invoicingProjectAccounts = this.enrichAccounts(this.invoicingProjectAccounts ?? []);
        this.refreshFilteredAccounts();
      },
      error: (err: any) => console.error('Failed to load clients', err),
    });
    this.accountService.getProjectsForDropdown().subscribe({
      next: (data) => {
        this.allProjects = data;
        this.projectOptions = data;
        this.invoicingProjectAccounts = this.enrichAccounts(this.invoicingProjectAccounts ?? []);
        this.refreshFilteredAccounts();
      },
      error: (err: any) => console.error('Failed to load projects', err),
    });
    this.currencyService.getActiveCurrencies().subscribe({
      next: (currencies) => {
        this.currencyOptions = currencies
          .filter((c) => c.currencyName)
          .map((c) => ({ label: c.currencyName, value: c.currencyName }));
      },
      error: (err: any) => console.error('Failed to load currencies', err),
    });
  }
  onClientsChange(event: { value: number[] | null }) {
    const clientIds = event.value ?? [];
    if (clientIds.length) {
      const selectedClientIds = new Set(clientIds);
      this.projectOptions = this.allProjects.filter(
        (project) => project.client?.id != null && selectedClientIds.has(project.client.id),
      );
    } else {
      this.projectOptions = this.allProjects;
    }
    const availableProjectIds = new Set(this.projectOptions.map((project) => project.id));
    const selectedProjectIds = (this.form.get('projectIds')?.value ?? []) as number[];
    const retainedProjectIds = selectedProjectIds.filter((projectId) => availableProjectIds.has(projectId));
    this.form.get('projectIds')?.setValue(retainedProjectIds);
    this.updateCurrencyFromProjects(retainedProjectIds);
  }
  onProjectsChange(event: { value: number[] | null }) {
    this.updateCurrencyFromProjects(event.value ?? []);
  }
  private updateCurrencyFromProjects(projectIds: number[]) {
    const selectedProjectIds = new Set(projectIds);
    const currencies = [
      ...new Set(
        this.allProjects
          .filter((project) => selectedProjectIds.has(project.id))
          .map((project) => project.currencyName?.trim())
          .filter((currency): currency is string => Boolean(currency)),
      ),
    ];
    this.form.get('currency')?.setValue(currencies.length === 1 ? currencies[0] : null);
  }
  openCreateDialog() {
    this.isEditMode = false;
    this.currentId = null;
    this.form.reset({
      clientIds: [],
      projectIds: [],
      status: InvoicingProjectAccountStatus.ACTIVE,
      startDate: new Date(),
    });
    this.projectOptions = this.allProjects;
    this.editDialogVisible = true;
  }
  openEditDialog(account: InvoicingProjectAccount) {
    this.isEditMode = true;
    this.currentId = account.id;
    const projectIds = [...(account.projectIds ?? [])];
    const selectedProjectIds = new Set(projectIds);
    const clientIds = [
      ...new Set(
        this.allProjects
          .filter((project) => selectedProjectIds.has(project.id))
          .map((project) => project.client?.id)
          .filter((clientId): clientId is number => clientId != null),
      ),
    ];
    const selectedClientIds = new Set(clientIds);
    this.projectOptions = clientIds.length
      ? this.allProjects.filter((project) => project.client?.id != null && selectedClientIds.has(project.client.id))
      : this.allProjects;
    this.form.patchValue({
      clientIds,
      projectIds,
      code: account.code,
      description: account.description ?? null,
      currency: account.currency ?? null,
      startDate: account.startDate ? new Date(account.startDate) : null,
      endDate: account.endDate ? new Date(account.endDate) : null,
      status: account.status,
    });
    this.editDialogVisible = true;
  }
  closeDialog() {
    this.editDialogVisible = false;
    this.isEditMode = false;
    this.form.reset({ clientIds: [], projectIds: [] });
    this.projectOptions = this.allProjects;
  }
  onImportFile() {
    if (this.importInProgress) return;
    this.ibbiolFileInput?.nativeElement?.click();
  }
  onIbbiolFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    input.value = '';
    if (!file) return;
    if (file.size > this.maxImportFileSizeBytes) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('INVOICING.IMPORT.FILE_TOO_LARGE'),
        detail: this.translate.instant('INVOICING.IMPORT.FILE_TOO_LARGE_DETAIL'),
      });
      return;
    }
    this.importInProgress = true;
    // Always use backend import so CSV/XLSX follow the same rules and
    // can benefit from backend-side project resolution/creation.
    this.importViaBackend(file, false);
  }
  private importTextWithLocalParser(file: File) {
    const reader = new FileReader();
    reader.onerror = () => {
      this.importInProgress = false;
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('INVOICING.IMPORT.FAILED'),
        detail: this.translate.instant('INVOICING.IMPORT.UNREADABLE_FILE'),
      });
    };
    reader.onload = () => {
      const rows = this.parseIbbiolText(String(reader.result ?? ''));
      if (!rows.length) {
        this.importInProgress = false;
        this.messageService.add({
          severity: 'warn',
          summary: this.translate.instant('INVOICING.IMPORT.NO_DATA'),
          detail: this.translate.instant('INVOICING.IMPORT.NO_VALID_ROWS_SELECTED_FILE'),
        });
        return;
      }
      this.importIbbiolRows(rows);
    };
    reader.readAsText(file);
  }
  private importViaBackend(file: File, allowFrontFallback: boolean) {
    this.accountService.startIbbiolImportJob(file).subscribe({
      next: (job: any) => {
        const jobId = String(job?.jobId ?? '').trim();
        if (!jobId) {
          this.importInProgress = false;
          this.importProgressText = null;
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('INVOICING.IMPORT.FAILED'),
            detail: this.translate.instant('INVOICING.IMPORT.NO_JOB_ID'),
          });
          return;
        }
        this.importProgressText = this.translate.instant('INVOICING.IMPORT.QUEUED');
        this.lastImportedSeen = 0;
        this.lastLiveRefreshAt = 0;
        this.pollIbbiolImportJob(jobId);
      },
      error: (err: any) => {
        const detail = err?.error?.message ?? err?.message ?? this.translate.instant('INVOICING.IMPORT.BACKEND_FAILED');
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('INVOICING.IMPORT.FAILED'),
          detail,
        });
        this.importInProgress = false;
        this.importProgressText = null;
      },
    });
  }
  private pollIbbiolImportJob(jobId: string) {
    this.importPollingSub?.unsubscribe();
    this.importPollingSub = timer(0, 2000)
      .pipe(switchMap(() => this.accountService.getIbbiolImportJobStatus(jobId)))
      .subscribe({
        next: (res: any) => {
          const status = String(res?.status ?? '').toUpperCase();
          if (status === 'QUEUED' || status === 'RUNNING') {
            const imported = Number(res?.imported ?? 0);
            const total = Number(res?.totalRows ?? 0);
            this.importProgressText =
              total > 0
                ? this.translate.instant('INVOICING.IMPORT.RUNNING_PROGRESS', { imported, total })
                : this.translate.instant('INVOICING.IMPORT.RUNNING_IMPORTED', { imported });
            const now = Date.now();
            const hasNewImportedRows = imported > this.lastImportedSeen;
            const canRefresh = now - this.lastLiveRefreshAt >= 3000;
            if (hasNewImportedRows && canRefresh) {
              this.lastImportedSeen = imported;
              this.lastLiveRefreshAt = now;
              this.loadAccounts();
            }
            return;
          }
          this.importPollingSub?.unsubscribe();
          this.importPollingSub = undefined;
          const imported = Number(res?.imported ?? 0);
          const skipped = Number(res?.skipped ?? 0);
          const failed = Number(res?.failed ?? 0);
          const total = Number(res?.totalRows ?? imported + skipped + failed);
          const firstError = Array.isArray(res?.errors) && res.errors.length ? String(res.errors[0]) : '';
          const errorHint = imported === 0 && firstError ? ` | ${firstError}` : '';
          if (status === 'COMPLETED') {
            this.messageService.add({
              severity: imported > 0 ? 'success' : 'warn',
              summary: imported > 0 ? this.translate.instant('INVOICING.IMPORT.COMPLETE') : this.translate.instant('INVOICING.IMPORT.FINISHED'),
              detail: this.translate.instant('INVOICING.IMPORT.SUMMARY', { total, imported, skipped, failed }) + errorHint,
            });
            if (imported > 0) this.loadAccounts();
          } else {
            const detail = firstError || String(res?.message ?? this.translate.instant('INVOICING.IMPORT.FAILED_DETAIL'));
            this.messageService.add({
              severity: 'error',
              summary: this.translate.instant('INVOICING.IMPORT.FAILED'),
              detail,
            });
          }
          this.importInProgress = false;
          this.importProgressText = null;
          this.lastImportedSeen = 0;
          this.lastLiveRefreshAt = 0;
        },
        error: (err: any) => {
          this.importPollingSub?.unsubscribe();
          this.importPollingSub = undefined;
          const detail = err?.error?.message ?? err?.message ?? this.translate.instant('INVOICING.IMPORT.STATUS_FAILED');
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('INVOICING.IMPORT.FAILED'),
            detail,
          });
          this.importInProgress = false;
          this.importProgressText = null;
          this.lastImportedSeen = 0;
          this.lastLiveRefreshAt = 0;
        },
      });
  }
  private importExcelWithFallbackParser(file: File) {
    // XLSX must be read as ArrayBuffer; readAsText causes binary "PK..." corruption.
    const reader = new FileReader();
    reader.onerror = () => {
      this.importInProgress = false;
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('INVOICING.IMPORT.FAILED'),
        detail: this.translate.instant('INVOICING.IMPORT.UNREADABLE_EXCEL'),
      });
    };
    reader.onload = () => {
      const buffer = reader.result as ArrayBuffer;
      const rows = this.parseIbbiolWorkbook(buffer);
      if (!rows.length) {
        this.importInProgress = false;
        this.messageService.add({
          severity: 'warn',
          summary: this.translate.instant('INVOICING.IMPORT.NO_DATA'),
          detail: this.translate.instant('INVOICING.IMPORT.NO_VALID_ROWS_EXCEL'),
        });
        return;
      }
      // For XLSX, bypass /import-ibbiol parser and import through validated account creation flow.
      this.importIbbiolRows(rows);
    };
    reader.readAsArrayBuffer(file);
  }
  private importRowsInChunks(
    rows: Array<{
      clientName: string;
      projectName: string;
      code: string;
      description: string | null;
      currency: string | null;
      startDate: string | null;
      endDate?: string | null;
      status?: string | null;
    }>,
    originalName: string,
  ) {
    const chunks: (typeof rows)[] = [];
    for (let index = 0; index < rows.length; index += this.importChunkSize) {
      chunks.push(rows.slice(index, index + this.importChunkSize));
    }
    let totalRows = 0;
    let imported = 0;
    let skipped = 0;
    let failed = 0;
    from(chunks)
      .pipe(
        concatMap((chunk, chunkIndex) => {
          const chunkFile = this.buildCsvFileFromRows(chunk, `${originalName}.part-${chunkIndex + 1}`);
          return this.accountService
            .importIbbiolFile(chunkFile)
            .pipe(catchError(() => of({ totalRows: chunk.length, imported: 0, skipped: 0, failed: chunk.length })));
        }),
        toArray(),
        finalize(() => {
          this.importInProgress = false;
        }),
      )
      .subscribe((results: any[]) => {
        for (const res of results) {
          totalRows += Number(res?.totalRows ?? 0);
          imported += Number(res?.imported ?? 0);
          skipped += Number(res?.skipped ?? 0);
          failed += Number(res?.failed ?? 0);
        }
        this.messageService.add({
          severity: imported > 0 ? 'success' : 'warn',
          summary: imported > 0 ? this.translate.instant('INVOICING.IMPORT.COMPLETE') : this.translate.instant('INVOICING.IMPORT.FINISHED'),
          detail: this.translate.instant('INVOICING.IMPORT.SUMMARY', { total: totalRows, imported, skipped, failed }),
        });
        if (imported > 0) this.loadAccounts();
      });
  }
  private buildCsvFileFromRows(
    rows: Array<{
      clientName: string;
      projectName: string;
      code: string;
      description: string | null;
      currency: string | null;
      startDate: string | null;
      endDate?: string | null;
      status?: string | null;
    }>,
    originalName: string,
  ): File {
    const escapeCsv = (v: string | null | undefined) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const header = [
      'Client Account',
      'Project',
      'Project Code',
      'Description',
      'Currency',
      'Start Date',
      'End Date',
      'Project Status',
    ];
    const lines = rows.map((r) =>
      [
        r.clientName,
        r.projectName,
        r.code,
        r.description ?? '',
        r.currency ?? '',
        r.startDate ?? '',
        r.endDate ?? '',
        r.status ?? '',
      ]
        .map(escapeCsv)
        .join(','),
    );
    const csv = [header.join(','), ...lines].join('\n');
    const baseName = (originalName || 'ibbiol').replace(/\.[^.]+$/, '');
    return new File([csv], `${baseName}.csv`, { type: 'text/csv;charset=utf-8' });
  }
  private buildCsvFileFromWorkbook(buffer: ArrayBuffer, originalName: string): File {
    const workbook = XLSX.read(buffer, { type: 'array' });
    const sheetName = this.selectBestSheetName(workbook);
    const sheet = sheetName ? workbook.Sheets[sheetName] : null;
    const grid = sheet ? (XLSX.utils.sheet_to_json<any[]>(sheet, { header: 1, defval: '' }) as any[][]) : [];
    const normalizeKey = (k: unknown) =>
      String(k ?? '')
        .replace(/\uFEFF/g, '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[_./-]/g, ' ')
        .replace(/[^a-z0-9\s]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    const looksLike = (value: string, aliases: string[]) => {
      const normalized = normalizeKey(value);
      return aliases.some((a) => {
        const alias = normalizeKey(a);
        return normalized === alias || normalized.startsWith(alias) || normalized.includes(alias);
      });
    };
    const codeAliases = ['project code', 'project account', 'account code', 'code', 'codigo proyecto', 'code projet'];
    const projectAliases = ['project', 'project name', 'proyecto', 'projet', 'nom projet'];
    const clientAliases = [
      'client account',
      'client',
      'client name',
      'bill to client',
      'customer',
      'customer account',
      'cliente',
      'cuenta cliente',
      'compte client',
    ];
    let headerRowIndex = 0;
    const maxScan = Math.min(120, grid.length);
    for (let index = 0; index < maxScan; index++) {
      const headers = (grid[index] ?? []).map((v) => String(v ?? ''));
      const hasCode = headers.some((h) => looksLike(h, codeAliases));
      const hasProject = headers.some((h) => looksLike(h, projectAliases));
      const hasClient = headers.some((h) => looksLike(h, clientAliases));
      if (hasCode && hasProject && hasClient) {
        headerRowIndex = index;
        break;
      }
    }
    const trimmedGrid = grid
      .slice(headerRowIndex)
      .filter((row) => (row ?? []).some((cell) => String(cell ?? '').trim().length > 0));
    const sheetForCsv = XLSX.utils.aoa_to_sheet(trimmedGrid);
    const csv = trimmedGrid.length ? XLSX.utils.sheet_to_csv(sheetForCsv, { FS: ',', RS: '\n' }) : '';
    const baseName = (originalName || 'ibbiol').replace(/\.[^.]+$/, '');
    return new File([csv], `${baseName}.csv`, { type: 'text/csv;charset=utf-8' });
  }
  private selectBestSheetName(workbook: XLSX.WorkBook): string | null {
    const names = workbook.SheetNames ?? [];
    if (!names.length) return null;
    const normalize = (v: unknown) =>
      String(v ?? '')
        .toLowerCase()
        .replace(/\uFEFF/g, '')
        .replace(/[.]/g, '')
        .replace(/[_-]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    const aliases = {
      code: ['project code', 'project account', 'code'],
      project: ['project', 'project name'],
      client: ['client account', 'client', 'bill to client'],
      start: ['start date', 'start'],
      end: ['end date', 'end'],
    };
    const headerHitScore = (headers: string[]) => {
      const has = (list: string[]) =>
        headers.some((h) => list.some((a) => h === a || h.includes(a) || h.startsWith(a)));
      let score = 0;
      if (has(aliases.code)) score += 4;
      if (has(aliases.project)) score += 3;
      if (has(aliases.client)) score += 3;
      if (has(aliases.start)) score += 1;
      if (has(aliases.end)) score += 1;
      return score;
    };
    let best: string | null = null;
    let bestScore = -1;
    for (const name of names) {
      const sheet = workbook.Sheets[name];
      if (!sheet) continue;
      const grid = XLSX.utils.sheet_to_json<any[]>(sheet, { header: 1, defval: '' }) as any[][];
      const scanRows = Math.min(80, grid.length);
      let bestHeaderScore = 0;
      for (let row = 0; row < scanRows; row++) {
        const headers = (grid[row] ?? []).map(normalize).filter(Boolean);
        bestHeaderScore = Math.max(bestHeaderScore, headerHitScore(headers));
      }
      const ref = String(sheet['!ref'] ?? '');
      if (!ref) continue;
      const range = XLSX.utils.decode_range(ref);
      const rows = range.e.r - range.s.r + 1;
      const cols = range.e.c - range.s.c + 1;
      const areaScore = Math.min(rows * cols, 200000);
      const score = bestHeaderScore * 1000000 + areaScore;
      if (score > bestScore) {
        bestScore = score;
        best = name;
      }
    }
    return best ?? names[0] ?? null;
  }
  private importIbbiolRows(
    rows: Array<{
      clientName: string;
      projectName: string;
      code: string;
      description: string | null;
      currency: string | null;
      startDate: string | null;
      endDate?: string | null;
      status?: string | null;
    }>,
  ) {
    if (rows.length === 0) {
      this.importInProgress = false;
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('INVOICING.IMPORT.NO_DATA'),
        detail: this.translate.instant('INVOICING.IMPORT.NO_VALID_ROWS'),
      });
      return;
    }
    const existingCodes = new Set(
      (this.invoicingProjectAccounts ?? []).map((a) => (a.code ?? '').trim()).filter(Boolean),
    );
    const rowsToImport = rows.filter((r) => !existingCodes.has(r.code));
    const skipped = rows.length - rowsToImport.length;
    from(rowsToImport)
      .pipe(
        concatMap((row) => {
          const resolved = this.resolveImportRow(row);
          if (!resolved.ok) {
            return of({ ok: false as const, error: resolved.error });
          }
          return this.accountService.createAccount(resolved.payload).pipe(
            concatMap(() => of({ ok: true as const })),
            catchError((err: any) => {
              const detail = err?.error?.message ?? err?.message ?? this.translate.instant('INVOICING.IMPORT.CREATE_ACCOUNT_FAILED');
              return of({ ok: false as const, error: detail });
            }),
          );
        }),
        toArray(),
        finalize(() => {
          this.importInProgress = false;
        }),
      )
      .subscribe((results) => {
        const successCount = results.filter((r) => r.ok).length;
        const failedCount = results.length - successCount;
        if (skipped > 0) {
          this.messageService.add({
            severity: 'info',
            summary: this.translate.instant('INVOICING.IMPORT.SKIPPED'),
            detail: this.translate.instant('INVOICING.IMPORT.SKIPPED_DETAIL', { count: skipped }),
          });
        }
        if (successCount > 0) {
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('INVOICING.IMPORT.COMPLETE'),
            detail: this.translate.instant('INVOICING.IMPORT.IMPORTED_DETAIL', { count: successCount }),
          });
          this.loadAccounts();
        }
        if (failedCount > 0) {
          const firstFailed = results.find((r) => !r.ok);
          const firstError =
            firstFailed && 'error' in firstFailed ? String(firstFailed.error ?? '') : this.translate.instant('INVOICING.IMPORT.SOME_ROWS_FAILED');
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('INVOICING.IMPORT.ISSUES'),
            detail: this.translate.instant('INVOICING.IMPORT.FAILED_ROWS_DETAIL', { count: failedCount, error: firstError }),
          });
        }
        if (successCount === 0 && failedCount === 0) {
          this.messageService.add({
            severity: 'info',
            summary: this.translate.instant('INVOICING.IMPORT.NOTHING_TO_IMPORT'),
            detail: this.translate.instant('INVOICING.IMPORT.ALL_PRESENT'),
          });
        }
      });
  }
  private isExcelFile(filename: string): boolean {
    const lower = (filename ?? '').toLowerCase();
    return lower.endsWith('.xlsx') || lower.endsWith('.xls');
  }
  private enrichAccounts(accounts: InvoicingProjectAccount[]): InvoicingProjectAccount[] {
    return (accounts ?? []).map((account) => ({
      ...account,
      projectName: this.joinRelationshipNames(account.projectNames) ?? account.projectName ?? null,
      oneErpClientName:
        this.joinRelationshipNames(account.oneErpClientNames) ?? account.oneErpClientName ?? null,
    }));
  }
  private joinRelationshipNames(names: Array<string | null> | null | undefined): string | null {
    const joined = [
      ...new Set(
        (names ?? [])
          .filter((name): name is string => typeof name === 'string')
          .map((name) => name.trim())
          .filter(Boolean),
      ),
    ].join(', ');
    return joined || null;
  }
  private parseIbbiolWorkbook(buffer: ArrayBuffer): Array<{
    clientName: string;
    projectName: string;
    code: string;
    description: string | null;
    currency: string | null;
    startDate: string | null;
    endDate?: string | null;
    status?: string | null;
  }> {
    const workbook = XLSX.read(buffer, { type: 'array' });
    const detectSheetByHeader = (): string | null => {
      const normalize = (value: unknown) =>
        String(value ?? '')
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase()
          .replace(/[_./-]/g, ' ')
          .replace(/[^a-z0-9\s]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
      const hasAny = (headers: string[], aliases: string[]) =>
        headers.some((h) => aliases.some((a) => h === a || h.startsWith(a) || h.includes(a)));
      const codeAliases = [
        'project code',
        'project account',
        'project co',
        'projectcode',
        'project_code',
        'account code',
      ];
      const projectAliases = ['project name', 'project'];
      const clientAliases = [
        'client account',
        'client acc',
        'bill to client',
        'bill to clie',
        'client name',
        'customer account',
      ];
      let bestName: string | null = null;
      let bestScore = -1;
      for (const name of workbook.SheetNames ?? []) {
        const sheet = workbook.Sheets[name];
        if (!sheet) continue;
        const grid = XLSX.utils.sheet_to_json<any[]>(sheet, { header: 1, defval: '' }) as any[][];
        const scanRows = Math.min(500, grid.length);
        let foundAt = -1;
        for (let row = 0; row < scanRows; row++) {
          const headers = (grid[row] ?? []).map(normalize).filter(Boolean);
          if (!headers.length) continue;
          if (hasAny(headers, codeAliases) && hasAny(headers, projectAliases) && hasAny(headers, clientAliases)) {
            foundAt = row;
            break;
          }
        }
        if (foundAt < 0) continue;
        const score = 10000 - foundAt + grid.length;
        if (score > bestScore) {
          bestScore = score;
          bestName = name;
        }
      }
      return bestName;
    };
    const sheetName = detectSheetByHeader() ?? this.selectBestSheetName(workbook);
    if (!sheetName) return [];
    const sheet = workbook.Sheets[sheetName];
    const normalizeKey = (k: unknown) =>
      String(k ?? '')
        .replace(/\uFEFF/g, '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[_./-]/g, ' ')
        .replace(/[^a-z0-9\s]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    const normalizeForMatch = (value: string) => normalizeKey(value).replace(/[_-]/g, ' ').replace(/\s+/g, ' ').trim();
    const headerLooksLike = (header: string, aliases: string[]) => {
      const h = normalizeForMatch(header);
      return aliases.some((alias) => {
        const a = normalizeForMatch(alias);
        if (h === a) return true;
        if (h.startsWith(`${a} `)) return true;
        if (h.startsWith(a) && a.length >= 6) return true;
        return a.length >= 8 && h.includes(a);
      });
    };
    const codeAliases = [
      'project code',
      'project account',
      'project co',
      'account code',
      'project_code',
      'projectcode',
      'codigo proyecto',
      'code projet',
    ];
    const projectAliases = ['project name', 'project', 'proyecto', 'projet', 'nom projet'];
    const clientAliases = [
      'client account',
      'client acc',
      'client name',
      'bill to client',
      'bill to clie',
      'bill to client party',
      'customer account',
      'cliente',
      'cuenta cliente',
      'compte client',
    ];
    const startAliases = ['start date', 'start', 'fecha inicio', 'date debut', 'debut'];
    const endAliases = ['end date', 'end', 'fecha fin', 'date fin'];
    const statusAliases = ['project status', 'status', 'estado', 'statut'];
    const countRecognizedHeaders = (headers: string[]) => {
      let hits = 0;
      if (headers.some((h) => headerLooksLike(h, codeAliases))) hits++;
      if (headers.some((h) => headerLooksLike(h, projectAliases))) hits++;
      if (headers.some((h) => headerLooksLike(h, clientAliases))) hits++;
      if (headers.some((h) => headerLooksLike(h, startAliases))) hits++;
      return hits;
    };
    // Read as array-of-arrays to handle cases where header is not on the first row.
    const rows = XLSX.utils.sheet_to_json<any[]>(sheet, { header: 1, defval: '' }) as any[][];
    if (!rows.length) return [];
    // Find a header row in the first 30 lines
    let headerRowIndex = -1;
    let headerIndex = new Map<string, number>();
    const maxScan = Math.min(400, rows.length);
    for (let i = 0; i < maxScan; i++) {
      const cols = rows[i] ?? [];
      const normalized = cols.map(normalizeKey);
      const hitCount = countRecognizedHeaders(normalized);
      if (hitCount >= 2) {
        headerRowIndex = i;
        headerIndex = new Map<string, number>();
        normalized.forEach((h, idx) => {
          if (h) headerIndex.set(h, idx);
        });
        break;
      }
    }
    const pickIdx = (names: string[], mustNotContain: string[] = []) => {
      let bestIdx = -1;
      let bestScore = -1;
      const blocked = mustNotContain.map(normalizeForMatch);
      for (const [headerName, idx] of headerIndex.entries()) {
        const normalizedHeader = normalizeForMatch(headerName);
        if (blocked.some((token) => token && normalizedHeader.includes(token))) continue;
        for (const alias of names) {
          const a = normalizeForMatch(alias);
          let score = -1;
          if (normalizedHeader === a) score = 100;
          else if (normalizedHeader.startsWith(`${a} `)) score = 90;
          else if (normalizedHeader.startsWith(a) && a.length >= 6) score = 80;
          else if (a.length >= 8 && normalizedHeader.includes(a)) score = 60;
          if (score > bestScore) {
            bestScore = score;
            bestIdx = idx;
          }
        }
      }
      return bestIdx;
    };
    const get = (cols: any[], idx: number) => (idx >= 0 ? String(cols[idx] ?? '').trim() : '');
    const idxClient = pickIdx([
      'client account',
      'client acc',
      'client name',
      'client_name',
      'bill to client',
      'bill to clie',
      'bill to client party',
      'bill-to client party',
      'customer account',
      'cliente',
      'cuenta cliente',
      'compte client',
    ]);
    const idxProject = pickIdx(
      ['project name', 'project', 'project_name', 'proyecto', 'projet', 'nom projet'],
      ['project type', 'project status', 'project code', 'profit center', 'cost center'],
    );
    const idxCode = pickIdx(
      [
        'project code',
        'project co',
        'project account',
        'project_account',
        'project_code',
        'account code',
        'codigo proyecto',
        'code projet',
      ],
      ['cost center', 'profit center', 'project status'],
    );
    const idxDescription = pickIdx(['description', 'desc']);
    const idxCurrency = pickIdx(['currency', 'curr']);
    const idxStartDate = pickIdx(['start date', 'start_date', 'start', 'fecha inicio', 'date debut']);
    const idxEndDate = pickIdx(['end date', 'end_date', 'end', 'fecha fin', 'date fin']);
    const idxStatus = pickIdx(['project status', 'status', 'estado', 'statut']);
    const startRow = headerRowIndex >= 0 ? headerRowIndex + 1 : 0;
    const out: Array<{
      clientName: string;
      projectName: string;
      code: string;
      description: string | null;
      currency: string | null;
      startDate: string | null;
      endDate?: string | null;
      status?: string | null;
    }> = [];
    for (let i = startRow; i < rows.length; i++) {
      const cols = rows[i] ?? [];
      const clientName = get(cols, idxClient);
      const projectName = get(cols, idxProject);
      const code = get(cols, idxCode);
      if (!clientName || !projectName || !code) continue;
      const description = (get(cols, idxDescription) || '').trim() || null;
      const currency = (get(cols, idxCurrency) || '').trim() || null;
      const startDate = (get(cols, idxStartDate) || '').trim() || null;
      const endDate = (get(cols, idxEndDate) || '').trim() || null;
      const status = (get(cols, idxStatus) || '').trim() || null;
      out.push({ clientName, projectName, code, description, currency, startDate, endDate, status });
    }
    if (out.length > 0) return out;
    // Fallback for private IBBIOL layouts where headers are shifted far to the right.
    // We detect column positions from a header row; if not found we fallback to legacy fixed columns.
    const positionalOut: Array<{
      clientName: string;
      projectName: string;
      code: string;
      description: string | null;
      currency: string | null;
      startDate: string | null;
      endDate?: string | null;
      status?: string | null;
    }> = [];
    const sheetRef = String(sheet['!ref'] ?? '');
    const range = sheetRef ? XLSX.utils.decode_range(sheetRef) : null;
    if (!range) return positionalOut;
    const readCell = (rowNumber: number, colIndex: number): string => {
      if (colIndex < 0) return '';
      const address = `${XLSX.utils.encode_col(colIndex)}${rowNumber}`;
      const cell = sheet[address];
      if (!cell) return '';
      const formatted = XLSX.utils.format_cell(cell);
      return String(formatted ?? cell.v ?? '').trim();
    };
    const findAliasColIndex = (rowValues: string[], aliases: string[], baseColIndex: number): number => {
      for (let columnIndex = 0; columnIndex < rowValues.length; columnIndex++) {
        if (headerLooksLike(rowValues[columnIndex] ?? '', aliases)) return baseColIndex + columnIndex;
      }
      return -1;
    };
    let headerRow = -1;
    let codeCol = -1;
    let projectCol = -1;
    let clientCol = -1;
    let startCol = -1;
    let endCol = -1;
    let statusCol = -1;
    const maxHeaderScanRow = Math.min(range.e.r + 1, range.s.r + 400);
    for (let rowNumber = range.s.r + 1; rowNumber <= maxHeaderScanRow; rowNumber++) {
      const rowValues: string[] = [];
      for (let colIndex = range.s.c; colIndex <= range.e.c; colIndex++) {
        rowValues.push(readCell(rowNumber, colIndex));
      }
      const normalized = rowValues.map(normalizeKey).filter(Boolean);
      if (countRecognizedHeaders(normalized) < 2) continue;
      const foundCode = findAliasColIndex(rowValues, codeAliases, range.s.c);
      const foundProject = findAliasColIndex(rowValues, projectAliases, range.s.c);
      const foundClient = findAliasColIndex(rowValues, clientAliases, range.s.c);
      if (foundCode < 0 || foundProject < 0 || foundClient < 0) continue;
      headerRow = rowNumber;
      codeCol = foundCode;
      projectCol = foundProject;
      clientCol = foundClient;
      startCol = findAliasColIndex(rowValues, startAliases, range.s.c);
      endCol = findAliasColIndex(rowValues, endAliases, range.s.c);
      statusCol = findAliasColIndex(rowValues, statusAliases, range.s.c);
      break;
    }
    if (codeCol < 0) codeCol = XLSX.utils.decode_col('B');
    if (projectCol < 0) projectCol = XLSX.utils.decode_col('C');
    if (clientCol < 0) clientCol = XLSX.utils.decode_col('T');
    if (startCol < 0) startCol = XLSX.utils.decode_col('BN');
    if (endCol < 0) endCol = XLSX.utils.decode_col('BO');
    if (statusCol < 0) statusCol = XLSX.utils.decode_col('AZ');
    const startPositionalRow = headerRow > 0 ? headerRow + 1 : range.s.r + 1;
    for (let rowNumber = startPositionalRow; rowNumber <= range.e.r + 1; rowNumber++) {
      const code = readCell(rowNumber, codeCol);
      const projectName = readCell(rowNumber, projectCol);
      const clientName = readCell(rowNumber, clientCol);
      const codeLower = code.toLowerCase();
      const projectLower = projectName.toLowerCase();
      const clientLower = clientName.toLowerCase();
      if (
        codeLower.includes('project code') ||
        codeLower.includes('project account') ||
        projectLower === 'project' ||
        clientLower.includes('client')
      ) {
        continue;
      }
      if (!code || !projectName || !clientName) continue;
      const startDate = readCell(rowNumber, startCol) || null;
      const endDate = readCell(rowNumber, endCol) || null;
      const status = readCell(rowNumber, statusCol) || null;
      positionalOut.push({
        clientName,
        projectName,
        code,
        description: null,
        currency: null,
        startDate,
        endDate,
        status,
      });
    }
    if (positionalOut.length > 0) return positionalOut;
    // Heuristic fallback: detect rows by Project Account pattern and known client/project names.
    const normalizeName = (value: string) =>
      value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();
    const clientsByNormalized = new Map<string, string>(
      (this.clientOptions ?? []).filter((c) => !!c?.name).map((c) => [normalizeName(String(c.name)), String(c.name)]),
    );
    const projectsByNormalized = new Map<string, string>(
      (this.allProjects ?? []).filter((p) => !!p?.name).map((p) => [normalizeName(String(p.name)), String(p.name)]),
    );
    const clientNames = Array.from(clientsByNormalized.keys());
    const projectNames = Array.from(projectsByNormalized.keys());
    const findByContains = (normalizedValue: string, allNames: string[]): string | null => {
      if (!normalizedValue) return null;
      let best: string | null = null;
      for (const name of allNames) {
        if (normalizedValue === name || normalizedValue.includes(name) || name.includes(normalizedValue)) {
          if (!best || name.length > best.length) best = name;
        }
      }
      return best;
    };
    const heuristicOut: Array<{
      clientName: string;
      projectName: string;
      code: string;
      description: string | null;
      currency: string | null;
      startDate: string | null;
      endDate?: string | null;
      status?: string | null;
    }> = [];
    const isLikelyHeaderText = (value: string): boolean => {
      const v = normalizeName(value);
      if (!v) return true;
      return (
        v.includes('project code') ||
        v === 'project' ||
        v.includes('project type') ||
        v.includes('project status') ||
        v.includes('client account') ||
        v.includes('bill to client') ||
        v.includes('cost center') ||
        v.includes('profit center') ||
        v.includes('start date') ||
        v.includes('end date')
      );
    };
    const pickNearestText = (rowValues: string[], codeIndex: number, offsets: number[]): string | null => {
      for (const offset of offsets) {
        const value = String(rowValues[codeIndex + offset] ?? '').trim();
        if (!value) continue;
        if (this.projectAccountPattern.test(value.toUpperCase())) continue;
        if (isLikelyHeaderText(value)) continue;
        return value;
      }
      return null;
    };
    for (const row of rows) {
      const rowValues = (row ?? []).map((v) => String(v ?? '').trim());
      const values = rowValues.filter(Boolean);
      if (!values.length) continue;
      const codeIndex = rowValues.findIndex((v) => this.projectAccountPattern.test(v.toUpperCase()));
      const code = codeIndex >= 0 ? rowValues[codeIndex].toUpperCase() : '';
      if (!code) continue;
      let projectName: string | null = null;
      let clientName: string | null = null;
      for (const value of values) {
        const key = normalizeName(value);
        if (!projectName && projectsByNormalized.has(key)) projectName = projectsByNormalized.get(key) ?? null;
        if (!clientName && clientsByNormalized.has(key)) clientName = clientsByNormalized.get(key) ?? null;
      }
      // Try fuzzy mapping when exact name lookup fails.
      if (!projectName) {
        for (const value of values) {
          const normalized = normalizeName(value);
          const match = findByContains(normalized, projectNames);
          if (match) {
            projectName = projectsByNormalized.get(match) ?? null;
            break;
          }
        }
      }
      if (!clientName) {
        for (const value of values) {
          const normalized = normalizeName(value);
          const match = findByContains(normalized, clientNames);
          if (match) {
            clientName = clientsByNormalized.get(match) ?? null;
            break;
          }
        }
      }
      // Positional rescue for typical IBBIOL layouts.
      if (!projectName && codeIndex >= 0) {
        const candidates = [rowValues[codeIndex + 1], rowValues[codeIndex + 2]];
        for (const candidate of candidates) {
          const normalized = normalizeName(String(candidate ?? ''));
          if (!normalized) continue;
          const match = findByContains(normalized, projectNames);
          if (match) {
            projectName = projectsByNormalized.get(match) ?? (String(candidate ?? '').trim() || null);
            break;
          }
          if (!projectName && candidate) projectName = String(candidate).trim();
        }
      }
      if (!clientName && codeIndex >= 0) {
        const candidates = [rowValues[codeIndex + 18], rowValues[codeIndex + 19], rowValues[codeIndex + 17]];
        for (const candidate of candidates) {
          const normalized = normalizeName(String(candidate ?? ''));
          if (!normalized) continue;
          const match = findByContains(normalized, clientNames);
          if (match) {
            clientName = clientsByNormalized.get(match) ?? (String(candidate ?? '').trim() || null);
            break;
          }
          if (!clientName && candidate) clientName = String(candidate).trim();
        }
      }
      if (!projectName && codeIndex >= 0) {
        projectName = pickNearestText(rowValues, codeIndex, [1, -1, 2, -2, 3, -3]);
      }
      if (!clientName && codeIndex >= 0) {
        clientName = pickNearestText(rowValues, codeIndex, [-2, -3, -4, -1, 4, 5, 6]);
      }
      if (!projectName || !clientName) continue;
      heuristicOut.push({
        clientName,
        projectName,
        code,
        description: null,
        currency: null,
        startDate: null,
        endDate: null,
        status: null,
      });
    }
    if (heuristicOut.length > 0) return heuristicOut;
    return this.parseIbbiolWorkbookAllSheetsBruteforce(workbook);
  }
  private parseIbbiolWorkbookAllSheetsBruteforce(workbook: XLSX.WorkBook): Array<{
    clientName: string;
    projectName: string;
    code: string;
    description: string | null;
    currency: string | null;
    startDate: string | null;
    endDate?: string | null;
    status?: string | null;
  }> {
    const normalize = (value: unknown) =>
      String(value ?? '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[_./-]/g, ' ')
        .replace(/[^a-z0-9\s]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    const looksLikeHeader = (value: string) => {
      const v = normalize(value);
      return (
        !v ||
        v.includes('project code') ||
        v.includes('project co') ||
        v.includes('project type') ||
        v.includes('project status') ||
        v.includes('client account') ||
        v.includes('client acc') ||
        v.includes('bill to client') ||
        v.includes('cost center') ||
        v.includes('profit center') ||
        v.includes('start date') ||
        v.includes('end date') ||
        v.includes('legal entity') ||
        v.includes('country') ||
        v.includes('office')
      );
    };
    const clientMap = new Map<string, string>(
      (this.clientOptions ?? []).filter((c) => !!c?.name).map((c) => [normalize(c.name), String(c.name)]),
    );
    const projectMap = new Map<string, string>(
      (this.allProjects ?? []).filter((p) => !!p?.name).map((p) => [normalize(p.name), String(p.name)]),
    );
    const clientKeys = Array.from(clientMap.keys());
    const projectKeys = Array.from(projectMap.keys());
    const bestNameMatch = (value: string, keys: string[]): string | null => {
      const v = normalize(value);
      if (!v) return null;
      let best: string | null = null;
      for (const key of keys) {
        if (v === key || v.includes(key) || key.includes(v)) {
          if (!best || key.length > best.length) best = key;
        }
      }
      return best;
    };
    const pickProjectNearCode = (rowValues: string[], codeIndex: number): string | null => {
      const offsets = [1, -1, 2, -2, 3, -3, 4];
      for (const offset of offsets) {
        const value = String(rowValues[codeIndex + offset] ?? '').trim();
        if (!value || looksLikeHeader(value)) continue;
        if (this.projectAccountPattern.test(value.toUpperCase())) continue;
        const match = bestNameMatch(value, projectKeys);
        if (match) return projectMap.get(match) ?? value;
        return value;
      }
      return null;
    };
    const pickClientNearCode = (rowValues: string[], codeIndex: number): string | null => {
      const offsets = [18, 19, 17, 20, 16, -2, -3, -1, 4, 5];
      for (const offset of offsets) {
        const value = String(rowValues[codeIndex + offset] ?? '').trim();
        if (!value || looksLikeHeader(value)) continue;
        if (this.projectAccountPattern.test(value.toUpperCase())) continue;
        const match = bestNameMatch(value, clientKeys);
        if (match) return clientMap.get(match) ?? value;
        return value;
      }
      return null;
    };
    const dedupe = new Set<string>();
    const result: Array<{
      clientName: string;
      projectName: string;
      code: string;
      description: string | null;
      currency: string | null;
      startDate: string | null;
      endDate?: string | null;
      status?: string | null;
    }> = [];
    for (const sheetName of workbook.SheetNames ?? []) {
      const sheet = workbook.Sheets[sheetName];
      if (!sheet) continue;
      const grid = XLSX.utils.sheet_to_json<any[]>(sheet, { header: 1, defval: '' }) as any[][];
      for (const row of grid) {
        const rowValues = (row ?? []).map((value) => String(value ?? '').trim());
        const codeIndex = rowValues.findIndex((value) => this.projectAccountPattern.test(value.toUpperCase()));
        if (codeIndex < 0) continue;
        const code = rowValues[codeIndex].toUpperCase();
        const projectName = pickProjectNearCode(rowValues, codeIndex);
        const clientName = pickClientNearCode(rowValues, codeIndex);
        if (!projectName || !clientName) continue;
        const key = `${code}|${normalize(projectName)}|${normalize(clientName)}`;
        if (dedupe.has(key)) continue;
        dedupe.add(key);
        result.push({
          clientName,
          projectName,
          code,
          description: null,
          currency: null,
          startDate: null,
          endDate: null,
          status: null,
        });
      }
    }
    return result;
  }
  private parseIbbiolText(text: string): Array<{
    clientName: string;
    projectName: string;
    code: string;
    description: string | null;
    currency: string | null;
    startDate: string | null;
    endDate?: string | null;
    status?: string | null;
  }> {
    const normalized = (text ?? '').replace(/\r\n/g, '\n').trim();
    if (!normalized) return [];
    const lines = normalized
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l.length > 0);
    if (lines.length === 0) return [];
    const sample = lines.slice(0, 5).join('\n');
    const sep = this.detectSeparator(sample);
    const parseLine = (line: string) => line.split(sep).map((v) => v.trim().replace(/^"|"$/g, ''));
    const normalizeHeader = (h: string) =>
      String(h ?? '')
        .trim()
        .toLowerCase()
        .replace(/[.]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    const header = parseLine(lines[0]).map(normalizeHeader);
    const hasHeader = header.some((h) =>
      [
        'client',
        'client name',
        'client account',
        'project',
        'project name',
        'project account',
        'project code',
        'account',
        'code',
        'start date',
        'end date',
        'status',
        'project status',
      ].includes(h),
    );
    const startIndex = hasHeader ? 1 : 0;
    const indexOfHeader = (names: string[]) => {
      const normalizedNames = names.map(normalizeHeader);
      return header.findIndex((h) => normalizedNames.includes(h));
    };
    const idxClient = hasHeader
      ? indexOfHeader(['client', 'client name', 'client_name', 'client account', 'bill to client party'])
      : 0;
    const idxProject = hasHeader ? indexOfHeader(['project', 'project name', 'project_name']) : 1;
    const idxCode = hasHeader
      ? indexOfHeader(['project account', 'project code', 'account', 'code', 'project_account', 'project_code'])
      : 2;
    const idxDescription = hasHeader ? indexOfHeader(['description', 'desc']) : 3;
    const idxCurrency = hasHeader ? indexOfHeader(['currency', 'curr']) : 4;
    const idxStartDate = hasHeader ? indexOfHeader(['start date', 'start_date', 'start']) : 5;
    const idxEndDate = hasHeader ? indexOfHeader(['end date', 'end_date', 'end']) : 6;
    const idxStatus = hasHeader ? indexOfHeader(['project status', 'status']) : 7;
    const rows: Array<{
      clientName: string;
      projectName: string;
      code: string;
      description: string | null;
      currency: string | null;
      startDate: string | null;
      endDate?: string | null;
      status?: string | null;
    }> = [];
    for (let i = startIndex; i < lines.length; i++) {
      const cols = parseLine(lines[i]);
      const clientName = (cols[idxClient] ?? '').trim();
      const projectName = (cols[idxProject] ?? '').trim();
      const code = (cols[idxCode] ?? '').trim();
      if (!clientName || !projectName || !code) continue;
      rows.push({
        clientName,
        projectName,
        code,
        description: (cols[idxDescription] ?? '').trim() || null,
        currency: (cols[idxCurrency] ?? '').trim() || null,
        startDate: (cols[idxStartDate] ?? '').trim() || null,
        endDate: idxEndDate >= 0 ? (cols[idxEndDate] ?? '').trim() || null : null,
        status: idxStatus >= 0 ? (cols[idxStatus] ?? '').trim() || null : null,
      });
    }
    return rows;
  }
  private detectSeparator(sample: string): string {
    const comma = (sample.match(/,/g) ?? []).length;
    const semi = (sample.match(/;/g) ?? []).length;
    const tab = (sample.match(/\t/g) ?? []).length;
    if (semi >= comma && semi >= tab) return ';';
    if (comma >= semi && comma >= tab) return ',';
    return '\t';
  }
  private normalizeImportedName(value: string | null | undefined): string {
    return String(value ?? '')
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[_./-]/g, ' ')
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }
  private resolveImportRow(row: {
    clientName: string;
    projectName: string;
    code: string;
    description: string | null;
    currency: string | null;
    startDate: string | null;
    endDate?: string | null;
    status?: string | null;
  }): { ok: true; payload: InvoicingProjectAccountRequest } | { ok: false; error: string } {
    const rawClientName = row.clientName.trim();
    const normalizedClientName = this.normalizeImportedName(rawClientName);
    const client =
      this.clientOptions.find((c) => c.name?.trim().toLowerCase() === rawClientName.toLowerCase()) ??
      this.clientOptions.find((c) => this.normalizeImportedName(c.name) === normalizedClientName);
    if (!client) return { ok: false, error: this.translate.instant('INVOICING.IMPORT.ERRORS.UNKNOWN_CLIENT', { client: row.clientName }) };
    const rawProjectName = row.projectName.trim();
    const normalizedProjectName = this.normalizeImportedName(rawProjectName);
    const project =
      this.allProjects.find(
        (p) => p.name?.trim().toLowerCase() === rawProjectName.toLowerCase() && (p.client?.id ?? null) === client.id,
      ) ??
      this.allProjects.find(
        (p) => this.normalizeImportedName(p.name) === normalizedProjectName && (p.client?.id ?? null) === client.id,
      );
    if (!project) {
      return {
        ok: false,
        error: this.translate.instant('INVOICING.IMPORT.ERRORS.UNKNOWN_PROJECT', { client: row.clientName, project: row.projectName }),
      };
    }
    if (!this.projectAccountPattern.test(row.code)) {
      return { ok: false, error: this.translate.instant('INVOICING.IMPORT.ERRORS.INVALID_CODE_FORMAT', { code: row.code }) };
    }
    const startDate = row.startDate ? this.tryParseDate(row.startDate) : null;
    if (row.startDate && !startDate) {
      return { ok: false, error: this.translate.instant('INVOICING.IMPORT.ERRORS.INVALID_START_DATE', { code: row.code }) };
    }
    const inheritedStartDate = startDate ?? this.tryParseDate(project.startDate);
    if (!inheritedStartDate) {
      return {
        ok: false,
        error: this.translate.instant('INVOICING.IMPORT.ERRORS.MISSING_START_DATE', { code: row.code }),
      };
    }
    const endDate = row.endDate ? this.tryParseDate(row.endDate) : null;
    if (row.endDate && !endDate) {
      return { ok: false, error: this.translate.instant('INVOICING.IMPORT.ERRORS.INVALID_END_DATE', { code: row.code }) };
    }
    const inheritedEndDate = endDate ?? this.tryParseDate(project.endDate);
    const currency = (row.currency ?? '').trim() || project.currencyName?.trim() || null;
    return {
      ok: true,
      payload: {
        projectIds: [project.id],
        code: row.code,
        description: row.description,
        currency,
        startDate: this.formatDate(inheritedStartDate),
        endDate: inheritedEndDate ? this.formatDate(inheritedEndDate) : null,
        status: InvoicingProjectAccountStatus.ACTIVE,
      },
    };
  }
  private tryParseDate(value: string | Date | null | undefined): Date | null {
    if (value == null) return null;
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }
    const v = value.trim();
    if (!v) return null;
    // Excel serial date number (e.g. 45456)
    if (/^\d+(\.\d+)?$/.test(v)) {
      const parsed = XLSX.SSF.parse_date_code(Number(v));
      if (parsed && parsed.y && parsed.m && parsed.d) {
        return new Date(parsed.y, parsed.m - 1, parsed.d);
      }
    }
    // yyyy-MM-dd
    const iso = /^(\d{4})-(\d{2})-(\d{2})$/;
    const dmY = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    let d: Date;
    const isoMatch = v.match(iso);
    if (isoMatch) {
      d = new Date(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3]));
      return isNaN(d.getTime()) ? null : d;
    }
    const dmYMatch = v.match(dmY);
    if (dmYMatch) {
      d = new Date(Number(dmYMatch[3]), Number(dmYMatch[2]) - 1, Number(dmYMatch[1]));
      return isNaN(d.getTime()) ? null : d;
    }
    d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  }
  activateAccount(account: any) {
    this.accountService.activateAccount(account.id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('INVOICING.MESSAGES.ACTIVATED'),
          detail: this.translate.instant('INVOICING.MESSAGES.ACTIVATED_DETAIL', { code: account.code }),
        });
        this.loadAccounts();
      },
      error: (err: any) => this.handleSaveError(err, 'INVOICING.MESSAGES.ACTIVATION_FAILED'),
    });
  }
  deactivateAccount(account: any) {
    this.accountService.deactivateAccount(account.id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'info',
          summary: this.translate.instant('INVOICING.MESSAGES.DEACTIVATED'),
          detail: this.translate.instant('INVOICING.MESSAGES.DEACTIVATED_DETAIL', { code: account.code }),
        });
        this.loadAccounts();
      },
      error: (err: any) => this.handleSaveError(err, 'INVOICING.MESSAGES.DEACTIVATION_FAILED'),
    });
  }
  private formatDate(date: any): string {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  saveAccount() {
    if (this.form.invalid) return;
    const formValue = this.form.getRawValue() as {
      projectIds: number[] | null;
      code: string | null;
      description: string | null;
      currency: string | null;
      startDate: Date | string | null;
      endDate: Date | string | null;
      status: InvoicingProjectAccountStatus | string | null;
    };
    if (!formValue.code || !formValue.startDate || !formValue.status) return;
    const payload: InvoicingProjectAccountRequest = {
      projectIds: [...(formValue.projectIds ?? [])],
      code: formValue.code,
      description: formValue.description,
      currency: formValue.currency,
      startDate: this.formatDate(formValue.startDate),
      endDate: formValue.endDate ? this.formatDate(formValue.endDate) : null,
      status: formValue.status,
    };
    if (this.isEditMode && this.currentId) {
      this.accountService.updateAccount(this.currentId, payload).subscribe({
        next: () => this.handleSuccess('INVOICING.MESSAGES.ACCOUNT_UPDATED'),
        error: (err: any) => this.handleSaveError(err, 'common.updateFailed'),
      });
    } else {
      this.accountService.createAccount(payload).subscribe({
        next: () => this.handleSuccess('INVOICING.MESSAGES.ACCOUNT_CREATED'),
        error: (err: any) => this.handleSaveError(err, 'common.creationFailed'),
      });
    }
  }
  private handleSaveError(err: any, summaryKey: string) {
    const summary = this.translate.instant(summaryKey);
    const detail = err?.error?.message ?? err?.message ?? this.translate.instant('INVOICING.MESSAGES.UNEXPECTED_ERROR');
    this.messageService.add({ severity: 'error', summary, detail });
    console.error(summary, err);
  }
  confirmDelete(event: Event, account: InvoicingProjectAccount) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: this.translate.instant('INVOICING.DELETE_CONFIRM.MESSAGE', { code: account.code }),
      icon: 'pi pi-info-circle',
      header: this.translate.instant('INVOICING.DELETE_CONFIRM.TITLE'),
      acceptIcon: 'none',
      rejectIcon: 'none',
      acceptLabel: this.translate.instant('INVOICING.DELETE_CONFIRM.YES'),
      rejectLabel: this.translate.instant('INVOICING.DELETE_CONFIRM.NO'),
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.deleteAccount(account),
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: this.translate.instant('INVOICING.DELETE_CONFIRM.CANCELLED_SUMMARY'),
          detail: this.translate.instant('INVOICING.DELETE_CONFIRM.CANCELLED_DETAIL'),
          life: 3000,
        });
      },
    });
  }
  deleteAccount(account: InvoicingProjectAccount) {
    this.accountService.deleteAccount(account.id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('INVOICING.DELETE_CONFIRM.DELETED_SUMMARY'),
          detail: this.translate.instant('INVOICING.DELETE_CONFIRM.DELETED_DETAIL'),
        });
        this.loadAccounts();
      },
      error: (err: any) => this.handleSaveError(err, 'INVOICING.MESSAGES.DELETE_FAILED')
    });
  }
  private handleSuccess(msgKey: string) {
    this.messageService.add({
      severity: 'success',
      summary: this.translate.instant('common.toast.success'),
      detail: this.translate.instant(msgKey),
    });
    this.closeDialog();
    this.loadAccounts();
  }
  openIbbiolDialog(preProjectCode?: string) {
    this.ibbiolForm.reset();
    if (preProjectCode) {
      this.ibbiolForm.patchValue({ preProjectCode });
    }
    this.ibbiolDialogVisible = true;
  }
  closeIbbiolDialog() {
    this.ibbiolDialogVisible = false;
    this.ibbiolForm.reset();
    this.returnGroupId = null;
  }
  private applyIbbiolEntryFromRoute(): void {
    const params = this.route.snapshot.queryParamMap;
    if (params.get('openIbbiol') !== '1') {
      return;
    }
    const preProjectCode = params.get('preProjectCode')?.trim() || undefined;
    const returnGroupIdRaw = params.get('returnGroupId');
    this.returnGroupId = returnGroupIdRaw != null && returnGroupIdRaw !== '' ? Number(returnGroupIdRaw) : null;
    if (this.returnGroupId != null && !Number.isFinite(this.returnGroupId)) {
      this.returnGroupId = null;
    }
    this.openIbbiolDialog(preProjectCode);
    this.clearIbbiolEntryQueryParams();
  }
  private clearIbbiolEntryQueryParams(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        openIbbiol: null,
        preProjectCode: null,
        returnGroupId: null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
  saveIbbiolAccount() {
    if (this.ibbiolForm.invalid) return;
    const code = this.ibbiolForm.get('preProjectCode')?.value;
    const payload: InvoicingProjectAccountRequest = {
      code,
      status: InvoicingProjectAccountStatus.ACTIVE,
      startDate: this.formatDate(new Date()),
    };
    const returnGroupId = this.returnGroupId;
    this.accountService.createAccount(payload).subscribe({
      next: () => {
        this.loadAccounts();
        this.closeIbbiolDialog();
        if (returnGroupId != null) {
          this.router.navigate(['/finance/group'], {
            queryParams: {
              groupId: returnGroupId,
              openAddCombination: '1',
              prefillProjectCode: code,
            },
          });
          return;
        }
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.toast.success'),
          detail: this.translate.instant('INVOICING.MESSAGES.IBBIOL_CREATED_SUCCESS'),
        });
      },
      error: (err: any) => {
        const errorMsg = err?.error?.message ?? this.translate.instant('INVOICING.MESSAGES.IBBIOL_CREATE_FAILED');
        this.messageService.add({ severity: 'error', summary: this.translate.instant('common.toast.error'), detail: errorMsg });
        console.error('IBBIOL creation failed', err);
      },
    });
  }
}
